import { useCallback, useEffect, useRef } from "react";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import type { Environment } from "components/Environment/utils/types";
import { normalizeEnvironmentStatus } from "components/Environment/utils/environtmentUtils";
import { VerbBuildStatus } from "models/Workspace.model";

const PORT_POLL_INTERVAL_MS = 10_000;
const RUNNING_PORT_GRACE_PERIOD_MS = 60_000;

type PortPollingPhase = "transitioning" | "running" | "inactive";

function getPortPollingPhase(
  environment: Environment | null,
): PortPollingPhase {
  if (!environment) return "inactive";
  const status = normalizeEnvironmentStatus(environment.status);
  if (status !== null) {
    switch (status) {
      case EnvironmentStatus.PENDING:
      case EnvironmentStatus.PREPARING:
      case EnvironmentStatus.PROVISIONING:
      case EnvironmentStatus.BUILDING:
      case EnvironmentStatus.STARTING:
        return "transitioning";
      case EnvironmentStatus.RUNNING:
        return "running";
      default:
        return "inactive";
    }
  }

  const { object_status, lifecycle_status } =
    environment.instance?.status ?? {};
  if (
    object_status === "error" ||
    [
      "stopped",
      "stopping",
      "suspended",
      "suspending",
      "failed",
      "terminating",
      "terminated",
    ].includes(lifecycle_status ?? "")
  )
    return "inactive";
  if (
    object_status === "pending" ||
    ["pending", "starting", "restarting"].includes(lifecycle_status ?? "") ||
    environment.verbBuildStatus === VerbBuildStatus.Pending ||
    environment.verbBuildStatus === VerbBuildStatus.Building
  )
    return "transitioning";
  return lifecycle_status === "running" ? "running" : "inactive";
}

export function useEnvironmentPortPollingInterval(
  environment: Environment | null,
) {
  const key = JSON.stringify([
    environment?.environment_id,
    environment?.instance?.instance_id,
  ]);
  const phase = getPortPollingPhase(environment);
  const pollingWindow = useRef({
    key,
    phase,
    runningUntil: null as number | null,
  });

  useEffect(() => {
    const current = pollingWindow.current;
    if (current.key === key && current.phase === phase) return;
    pollingWindow.current = {
      key,
      phase,
      runningUntil:
        current.key === key &&
        current.phase === "transitioning" &&
        phase === "running"
          ? Date.now() + RUNNING_PORT_GRACE_PERIOD_MS
          : null,
    };
  }, [key, phase]);

  // React Query evaluates this callback as requests settle or options change.
  // Opening an already-running instance loads once without opening a grace period.
  return useCallback(() => {
    if (phase === "transitioning") return PORT_POLL_INTERVAL_MS;
    const current = pollingWindow.current;
    if (
      current.key !== key ||
      phase !== "running" ||
      current.runningUntil === null
    )
      return false;
    const remaining = current.runningUntil - Date.now();
    return remaining > 0 ? Math.min(PORT_POLL_INTERVAL_MS, remaining) : false;
  }, [key, phase]);
}
